// CDR Anomaly — operator security dashboard (#608).
// Shows call-volume spikes per tenant/hour, IRSF-flagged international calls,
// and spend guardrail status. System admins only (mirrors API gate).

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });

type ByHourRow = {
  domain_uuid: string;
  tenant_name: string;
  hour: string;
  call_count: number;
};

type IrsfRow = {
  domain_uuid: string;
  tenant_name: string;
  destination_number: string;
  created_at: string;
  duration_seconds: number | null;
};

type Guardrail = {
  domain_uuid: string;
  tenant_name: string;
  alert_usd: number | null;
  suspend_usd: number | null;
  install_error: string | null;
  last_alert_at: string | null;
  last_suspend_at: string | null;
};

type AnomalyData = {
  hours: number;
  byHour: ByHourRow[];
  irsf: IrsfRow[];
  guardrails: Guardrail[];
};

const HOUR_OPTIONS = [
  { value: 1,   label: 'Last 1 hour' },
  { value: 6,   label: 'Last 6 hours' },
  { value: 24,  label: 'Last 24 hours' },
  { value: 72,  label: 'Last 3 days' },
  { value: 168, label: 'Last 7 days' },
];

export default function CdrAnomaly() {
  const [hours, setHours] = useState(24);

  const { data, isLoading, refetch, isFetching, error } = useQuery<AnomalyData>({
    queryKey: ['cdr-anomaly', hours],
    queryFn: async () => {
      const r = await fetch(`/api/admin/security/cdr-anomaly?hours=${hours}`, { headers: auth() });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Shield size={22} /> Security — CDR Anomaly
        </h1>
        <div className="flex items-center gap-2">
          <select
            className="input text-sm"
            value={hours}
            onChange={e => setHours(Number(e.target.value))}
          >
            {HOUR_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button className="btn" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw size={14} className={`inline mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="card p-8 text-center text-textSecondary">Loading…</div>
      )}
      {error && (
        <div className="card p-4 text-sm bg-danger/10 border border-danger text-danger">
          {String((error as Error).message)}
        </div>
      )}

      {data && (
        <>
          {/* ── IRSF Flags ── */}
          <section>
            <h2 className="text-lg font-medium mb-2 flex items-center gap-2">
              {data.irsf.length > 0
                ? <AlertTriangle size={18} className="text-warning" />
                : <CheckCircle size={18} className="text-success" />
              }
              High-Risk International Calls (IRSF)
              {data.irsf.length > 0 && (
                <span className="ml-1 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-warning/20 text-warning">
                  {data.irsf.length}
                </span>
              )}
            </h2>
            {data.irsf.length === 0 ? (
              <div className="card p-4 text-sm text-textSecondary">
                No high-risk international calls detected in this window.
              </div>
            ) : (
              <div className="card p-0 overflow-hidden overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Destination</th>
                      <th>When</th>
                      <th>Duration (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.irsf.map((r, i) => (
                      <tr key={i} className="hover:bg-elevated">
                        <td>{r.tenant_name}</td>
                        <td className="font-mono text-warning">{r.destination_number}</td>
                        <td className="text-xs whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td>{r.duration_seconds ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Spend Guardrails ── */}
          <section>
            <h2 className="text-lg font-medium mb-2">Spend Guardrails</h2>
            {data.guardrails.length === 0 ? (
              <div className="card p-4 text-sm text-textSecondary">
                No spend guardrails configured. Set them up in Voice &amp; Chat → Trunk settings.
              </div>
            ) : (
              <div className="card p-0 overflow-hidden overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Alert ($)</th>
                      <th>Suspend ($)</th>
                      <th>Last Alert</th>
                      <th>Last Suspend</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.guardrails.map((g, i) => (
                      <tr key={i} className="hover:bg-elevated">
                        <td>{g.tenant_name}</td>
                        <td>{g.alert_usd != null ? `$${g.alert_usd}` : '—'}</td>
                        <td>{g.suspend_usd != null ? `$${g.suspend_usd}` : '—'}</td>
                        <td className="text-xs whitespace-nowrap">
                          {g.last_alert_at ? new Date(g.last_alert_at).toLocaleString() : '—'}
                        </td>
                        <td className="text-xs whitespace-nowrap">
                          {g.last_suspend_at ? new Date(g.last_suspend_at).toLocaleString() : '—'}
                        </td>
                        <td>
                          {g.install_error ? (
                            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-danger/20 text-danger"
                              title={g.install_error}>
                              error
                            </span>
                          ) : g.last_suspend_at ? (
                            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-warning/20 text-warning">
                              suspended
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-success/10 text-success">
                              ok
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Call Volume by Tenant & Hour ── */}
          <section>
            <h2 className="text-lg font-medium mb-2">Call Volume by Tenant &amp; Hour</h2>
            {data.byHour.length === 0 ? (
              <div className="card p-4 text-sm text-textSecondary">
                No call data in this window.
              </div>
            ) : (
              <div className="card p-0 overflow-hidden overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Hour (local)</th>
                      <th>Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byHour.slice(0, 200).map((r, i) => (
                      <tr key={i} className="hover:bg-elevated">
                        <td>{r.tenant_name}</td>
                        <td className="text-xs font-mono whitespace-nowrap">
                          {new Date(r.hour).toLocaleString()}
                        </td>
                        <td>
                          <span className={Number(r.call_count) > 50 ? 'text-warning font-semibold' : ''}>
                            {r.call_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.byHour.length > 200 && (
                  <div className="p-2 text-xs text-textSecondary border-t border-border">
                    Showing top 200 of {data.byHour.length} rows — narrow the window to see all.
                  </div>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
